import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";

import { IModal } from "../types/modal";
import { useModal } from "./useModal";

interface HandleLeavePromptProperties extends IModal {
  handleContinue: () => void;
}

export const useHandleLeavePrompt = (
  when: boolean,
  ignoredRefs?: React.MutableRefObject<any>[]
): HandleLeavePromptProperties => {
  const [targetLocation, setTargetLocation] = useState<any>(null);
  const { isModalVisible, toggleModalVisibility: toggle } = useModal();
  const history = useHistory();
  const unblockRef = useRef<any>(null);

  const clearBlocker = () => {
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
  };

  const handleRouteChangeClick = (e: MouseEvent) => {
    const isIgnored = ignoredRefs && ignoredRefs.find((ref) => ref.current.contains(e.target));

    if (isIgnored) {
      clearBlocker();
      unblockRef.current = history.block(() => {
        return;
      });
      return;
    }

    clearBlocker();
    unblockRef.current = history.block((location) => {
      if (when) {
        setTargetLocation(location);
        toggle();
        return false;
      }
      return;
    });
  };

  useEffect(() => {
    document.addEventListener("click", handleRouteChangeClick, true);
    return () => {
      document.removeEventListener("click", handleRouteChangeClick, true);
      clearBlocker();
    };
  }, [history, ignoredRefs, when]);

  const handleContinue = (): void => {
    clearBlocker();
    toggle();
    if (targetLocation) {
      history.push({
        pathname: targetLocation.pathname,
        search: targetLocation.search,
        hash: targetLocation.hash,
        state: targetLocation.state,
      });
    }
  };

  const toggleModalVisibility = () => {
    clearBlocker();
    unblockRef.current = history.block(() => {
      return;
    });
    toggle();
  };

  return {
    isModalVisible,
    toggleModalVisibility,
    handleContinue,
  };
};
